import { Component, inject, OnInit, Optional, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { CustomTableComponent } from 'src/app/components/custom-table/custom-table.component';
import { CheckBoxAction, ActionColor } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { MaintenanceSchedulingModel } from 'src/app/models/maintenance/maintenance-scheduling.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { MaintenanceSchedulingDetailTableModel } from 'src/app/models/tables/maintenance/maintenance-scheduling-detail-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { MaintenanceSchedulingDetailService } from 'src/app/services/maintenance/maintenance-scheduling-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-generate-maintenance-orders',
  templateUrl: './generate-maintenance-orders.component.html',
  styleUrls: ['./generate-maintenance-orders.component.scss'],
  standalone: false,
})
export class GenerateMaintenanceOrdersComponent implements OnInit {

  public config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly maintenanceSchedulingDetailService = inject(MaintenanceSchedulingDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  @Optional() private ref = inject(DynamicDialogRef);

  @ViewChild('customTable') customTable?: CustomTableComponent | null = null;

  ascendant: boolean = true;
  header: ColumnResponseModel[] = [];
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Agregar',
      icon: '',
      color: ActionColor.SUCCESS,
      action: 'add'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  dataMaintenanceSchedulingDetail: PagedResponseModel<MaintenanceSchedulingDetailTableModel> = new PagedResponseModel<MaintenanceSchedulingDetailTableModel>();
  disabledStart: boolean = false;
  filters: ColumnResponseModel[] = [];
  limit: number = 10;
  maintenanceSchedulingModel: MaintenanceSchedulingModel = new MaintenanceSchedulingModel();
  page: number = 1;
  selectedItems: any[] = [];

  subscription: Subscription;

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.maintenanceSchedulingModel = inputs.maintenanceSchedulingModel;
    });
    await Promise.all([
      this.getColumnsForPendingMaintenanceSchedulingByOrder(),
      this.getPendingMaintenanceSchedulingByOrder()
    ])
  }


  async getColumnsForPendingMaintenanceSchedulingByOrder() {
    try {
      this.header = await firstValueFrom(this.maintenanceSchedulingDetailService.getColumnsForPendingMaintenanceSchedulingByOrder());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getPendingMaintenanceSchedulingByOrder() {
    try {
      this.dataMaintenanceSchedulingDetail = await firstValueFrom(
        this.maintenanceSchedulingDetailService.getPendingMaintenanceSchedulingByOrder(
          this.maintenanceSchedulingModel.maintenanceSchedulingId, this.page, this.limit, this.ascendant, this.columnSort, this.filters));
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async generateMaintenanceOrder() {
    this.customTable?.returnSelectedData();
    if (this.selectedItems.length === 0) {
      this.toastHandlerService.showToast('warning', 'Debe seleccionar al menos una orden de mantenimiento.', 'Advertencia');
      return;
    }

    const accepted = await this.confirmDialogService.confirm('¿Realmente desea generar las órdenes de mantenimiento de los equipos seleccionados?');
    if (accepted) {
      try {
        const maintenanceSchedulingDetailsIds = this.selectedItems?.map(x => x.maintenanceSchedulingDetailId);
        const response = await firstValueFrom(this.maintenanceSchedulingDetailService.generateMaintenanceOrders(this.maintenanceSchedulingModel.maintenanceSchedulingId, maintenanceSchedulingDetailsIds));
        if (response) {
          this.toastHandlerService.showToast('success', 'Las órdenes de mantenimiento fueron generadas exitosamente');
          await this.getPendingMaintenanceSchedulingByOrder();
          this.onReloadEmit();
          this.selectedItems = [];
        } else {
          this.toastHandlerService.showToast('error', 'Error', 'Error al generar las órdenes de mantenimiento.');
        }
      } catch (error) {
        console.error('Error al generar las órdenes de mantenimiento.', error);
      }
    }
  }

  async selectionChange(event: { selectedItem?: any }) {
    this.selectedItems = event?.selectedItem?.filter((item: any) => item.id !== null);
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getPendingMaintenanceSchedulingByOrder();
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
